import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import useLimits from '../../hooks/useLimits';
import { RefreshCw, X } from 'lucide-react';
import { generateSummaryForSession, fetchSavedSummaries, isSummaryRequestInFlight } from '../../services/summaryService';

// Normalize plugin exports and use these in ReactMarkdown (prevents interop errors and fixes ESLint)
const safeRemarkMath = (typeof remarkMath === 'function' ? remarkMath : remarkMath?.default) || undefined;
const safeRehypeKatex = (typeof rehypeKatex === 'function' ? rehypeKatex : rehypeKatex?.default) || undefined;

export default function SummarySection({ title, videoDuration, contentType, transcriptOrContent, contentId }) {
    const [isGenerating, setIsGenerating] = useState(true);
    const [summary, setSummary] = useState(null);
    const [generationError, setGenerationError] = useState(null);
    const [summaryPreferences, setSummaryPreferences] = useState({
        prompt: 'Detailed Summary',
        timestamp: 'All'
    });

    const { summary: summaryLimit } = useLimits();
    const containerRef = useRef(null);

    const handleGenerateSummary = useCallback(async (preferences, forceRegenerate = false) => {
        // Avoid starting a new request if one is already in-flight for this contentId
        if (contentId && isSummaryRequestInFlight(contentId)) {
            return;
        }

        if (summaryLimit !== undefined && summaryLimit <= 0 && !forceRegenerate) {
            setGenerationError("You have reached your daily summary limit.");
            return;
        }

        const promptStyle = preferences?.prompt || summaryPreferences.prompt || 'Detailed Summary';
        setIsGenerating(true);
        setSummary(null);
        setGenerationError(null);

        if (preferences) {
            setSummaryPreferences({
                prompt: preferences.prompt,
                timestamp: preferences.rangeType === 'custom' ? 'Custom' : 'All'
            });
        }

        try {
            if (!contentId && !transcriptOrContent) {
                throw new Error("Missing contentId or transcript; cannot generate summary.");
            }

            const summaryData = await generateSummaryForSession(contentId, {
                promptStyle,
                includeTimestamps: contentType === 'youtube',
                ranges: preferences?.ranges || null
            });

            setSummary(summaryData);
        } catch (error) {
            setGenerationError(
                error?.message
                    ? `Failed to generate summary: ${error.message}`
                    : "Failed to generate summary. Please try again."
            );
        } finally {
            setIsGenerating(false);
        }
    }, [contentType, transcriptOrContent, contentId, summaryPreferences.prompt, summaryLimit]);

    useEffect(() => {
        let mounted = true;
        const init = async () => {
            if (!contentId) {
                if (mounted) handleGenerateSummary();
                return;
            }

            try {
                const savedList = await fetchSavedSummaries(contentId);
                if (mounted && Array.isArray(savedList) && savedList.length > 0) {
                    const getTime = (t) => {
                        if (!t) return 0;
                        if (typeof t.toMillis === 'function') return t.toMillis();
                        if (t.seconds) return t.seconds * 1000;
                        const parsed = Date.parse(String(t));
                        return isNaN(parsed) ? 0 : parsed;
                    };
                    let chosen = savedList[0];
                    if (savedList.some(s => s.timestamp)) {
                        chosen = savedList.reduce((a, b) =>
                            (getTime(a.timestamp) >= getTime(b.timestamp) ? a : b)
                        );
                    }
                    setSummary(chosen.summary || null);
                    setIsGenerating(false);
                    return;
                }
            } catch (e) {
                // ignore fetch errors
            }

            if (mounted) handleGenerateSummary();
        };
        init();
        return () => { mounted = false; };
    }, [handleGenerateSummary, contentId]);

    return (
        <div
            ref={containerRef}
            className="relative flex flex-col bg-white mb-10"
            style={{ height: "730px", overflowY: "auto", padding: "24px" }}
        >
            <div className="flex-grow">
                {isGenerating && (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <RefreshCw size={32} className="mx-auto animate-spin text-gray-500 mb-4" />
                            <p className="text-gray-600">Generating summary...</p>
                        </div>
                    </div>
                )}

                {generationError && !isGenerating && (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center max-w-md">
                            <X className="mx-auto h-12 w-12 text-red-500 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900">Failed to Generate Summary</h3>
                            <p className="text-sm text-gray-500 mt-2 mb-6">{generationError}</p>
                            <button
                                onClick={() => handleGenerateSummary(summaryPreferences, true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {summary && !isGenerating && (
                    <div className="notes-container animate-fade-in">
                        <h1 className="!text-2xl !font-bold !text-gray-900 !border-b-0 !pb-0 mb-6">{summary.title}</h1>
                        <ul>
                            {summary.points.map((point, i) => (
                                <li key={i} className="mb-4 leading-relaxed">
                                    <ReactMarkdown
                                        remarkPlugins={safeRemarkMath ? [safeRemarkMath] : []}
                                        rehypePlugins={safeRehypeKatex ? [safeRehypeKatex] : []}
                                    >
                                        {point.text}
                                    </ReactMarkdown>
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-gray-500 text-center mt-6">
                            AI-generated responses may not always be accurate. Verify important information.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}